import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ricApi, ricKeys } from '../ric.api';
import type { EdicionTomaPayload, RicFiltros, TomaPayload } from '../ric.types';

/** Tanques, umbral de diferencia y datos de empresa. */
export function useRicAuxiliares(enabled = true) {
  return useQuery({
    queryKey: ricKeys.aux,
    queryFn: ({ signal }) => ricApi.auxiliares(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Tomas del tanque en el rango indicado. */
export function useRicReporte(filtros: RicFiltros, enabled = true) {
  return useQuery({
    queryKey: ricKeys.reporte(filtros),
    queryFn: ({ signal }) => ricApi.reporte(filtros, signal),
    enabled,
    staleTime: 30_000,
  });
}

/** Detalle de una toma (con permisos de edición y anulación). */
export function useRicRegistro(id: number | null, enabled = true) {
  return useQuery({
    queryKey: ricKeys.registro(id ?? 0),
    queryFn: ({ signal }) => ricApi.registro(id as number, signal),
    enabled: enabled && Boolean(id),
  });
}

/** Última toma activa del tanque (para precargar la captura). */
export function useRicUltimaToma(tank: number | null, enabled = true) {
  return useQuery({
    queryKey: ricKeys.ultima(tank ?? 0),
    queryFn: ({ signal }) => ricApi.ultimaToma(tank as number, signal),
    enabled: enabled && Boolean(tank),
    staleTime: 60_000,
  });
}

/** Consolidado mensual por tanque. */
export function useRicVisual(anio: number | null, mes: number | null, enabled = true) {
  return useQuery({
    queryKey: ricKeys.visual(anio ?? 0, mes ?? 0),
    queryFn: ({ signal }) => ricApi.visual(anio as number, mes as number, signal),
    enabled: enabled && Boolean(anio) && Boolean(mes),
    staleTime: 5 * 60_000,
  });
}

/** Consolidado mensual por producto padre. */
export function useRicOsinerming(anio: number | null, mes: number | null, enabled = true) {
  return useQuery({
    queryKey: ricKeys.osinerming(anio ?? 0, mes ?? 0),
    queryFn: ({ signal }) => ricApi.osinerming(anio as number, mes as number, signal),
    enabled: enabled && Boolean(anio) && Boolean(mes),
    staleTime: 5 * 60_000,
  });
}

/** Invalida el módulo RIC (listado, detalle y consolidados). */
function useInvalidarRic() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: ricKeys.all });
  };
}

export function useCrearToma() {
  const invalidar = useInvalidarRic();

  return useMutation({
    mutationFn: (payload: TomaPayload) => ricApi.crearToma(payload),
    onSuccess: () => invalidar(),
  });
}

export function useEditarToma() {
  const invalidar = useInvalidarRic();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EdicionTomaPayload }) =>
      ricApi.editarToma(id, payload),
    onSuccess: () => invalidar(),
  });
}

export function useAnularToma() {
  const invalidar = useInvalidarRic();

  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => ricApi.anularToma(id, reason),
    onSuccess: () => invalidar(),
  });
}
